import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Bank, DeviceMobile, ArrowSquareOut } from "@phosphor-icons/react";

export default function Withdraw() {
  const { user, refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("momo");
  const [accountName, setAccountName] = useState("");
  const [accountDetails, setAccountDetails] = useState("");
  const [bankName, setBankName] = useState("");
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const [s, h] = await Promise.all([
      api.get("/settings"),
      api.get("/withdrawals/mine"),
    ]);
    setSettings(s.data);
    setHistory(h.data);
    refresh();
  }, [refresh]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (settings && amt < settings.min_withdrawal) {
      return toast.error(`Minimum withdrawal is $${settings.min_withdrawal.toFixed(2)}`);
    }
    if (amt > (user?.balance || 0)) return toast.error("Insufficient balance");
    if (!accountName.trim() || !accountDetails.trim()) {
      return toast.error("Fill in account details");
    }
    if (method === "bank" && !bankName.trim()) return toast.error("Enter bank name");

    setSubmitting(true);
    try {
      await api.post("/withdrawals", {
        amount: amt,
        method,
        account_name: accountName.trim(),
        account_details: accountDetails.trim(),
        bank_name: method === "bank" ? bankName.trim() : null,
      });
      toast.success("Withdrawal requested. Admin will process it.");
      setAmount("");
      setAccountName("");
      setAccountDetails("");
      setBankName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = (s) =>
    s === "paid"
      ? "border-emerald-500/40 text-emerald-400"
      : s === "rejected"
      ? "border-red-500/40 text-red-400"
      : "border-yellow-500/40 text-yellow-400";

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto space-y-6" data-testid="withdraw-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black">Withdraw</h1>
        <p className="text-white/60 mt-1">
          Cash out to Mobile Money or Bank. Admin manually processes each request.
        </p>
      </div>

      <div className="glass rounded-2xl p-6 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/50">Available</p>
          <p className="font-heading text-4xl font-black text-emerald-400 mt-1">
            ${(user?.balance || 0).toFixed(2)}
          </p>
        </div>
        {settings && (
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest font-bold text-white/50">Minimum</p>
            <p className="font-heading text-xl font-bold">${settings.min_withdrawal.toFixed(2)}</p>
          </div>
        )}
      </div>

      <Tabs defaultValue="new">
        <TabsList className="bg-white/5">
          <TabsTrigger data-testid="tab-new-withdrawal" value="new">Request</TabsTrigger>
          <TabsTrigger data-testid="tab-history" value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-4">
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="font-heading">New request</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-white/70">Amount (USD)</label>
                <Input
                  data-testid="withdraw-amount-input"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="10.00"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-white/70">Method</label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger
                    data-testid="withdraw-method-select"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    <SelectItem value="momo">
                      <div className="flex items-center gap-2">
                        <DeviceMobile size={16} weight="bold" /> Mobile Money
                      </div>
                    </SelectItem>
                    <SelectItem value="bank">
                      <div className="flex items-center gap-2">
                        <Bank size={16} weight="bold" /> Bank Transfer
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-white/70">Account name</label>
                <Input
                  data-testid="withdraw-account-name"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="Recipient full name"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-white/70">
                  {method === "momo" ? "MoMo phone number" : "Account number"}
                </label>
                <Input
                  data-testid="withdraw-account-details"
                  value={accountDetails}
                  onChange={(e) => setAccountDetails(e.target.value)}
                  placeholder={method === "momo" ? "+233..." : "1234567890"}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              {method === "bank" && (
                <div>
                  <label className="text-sm text-white/70">Bank name</label>
                  <Input
                    data-testid="withdraw-bank-name"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="e.g. GT Bank"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              )}
              <Button
                data-testid="withdraw-submit-btn"
                onClick={submit}
                disabled={submitting}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-heading font-bold text-base gap-2"
              >
                <ArrowSquareOut size={20} weight="bold" /> Request withdrawal
              </Button>
              <p className="text-xs text-white/50 text-center">
                Amount is deducted immediately and refunded if rejected.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-0">
              {history.length === 0 ? (
                <p className="text-white/50 text-sm text-center py-8">No requests yet.</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {history.map((w) => (
                    <div key={w.withdrawal_id} className="p-4 flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-bold text-lg">${w.amount.toFixed(2)}</p>
                        <p className="text-xs text-white/50">
                          {w.method.toUpperCase()} • {new Date(w.created_at).toLocaleString()}
                        </p>
                        {w.admin_note && (
                          <p className="text-xs text-white/60 mt-1 italic">{w.admin_note}</p>
                        )}
                      </div>
                      <Badge variant="outline" className={statusColor(w.status)}>
                        {w.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
